import React, { useState, useEffect } from 'react';
import { 
  FlaskConical, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Plus,
  Filter,
  Search,
  Target,
  BarChart3,
  Lightbulb,
  Activity
} from 'lucide-react';
import ExperimentDetailsModal from '../components/ExperimentDetailsModal';

interface Experiment {
  id: string;
  title: string;
  hypothesis: string;
  status: 'proposed' | 'running' | 'completed' | 'failed';
  confidence: number;
  impact: 'low' | 'medium' | 'high';
  signals: string[];
  startDate: string;
  expectedEnd: string;
  results?: string;
  createdFrom?: string;
  sourceInsight?: any;
  objective?: string;
  methodology?: string;
  targetAudience?: string;
  sampleSize?: number;
  owner?: string;
}

const ExperimentHub: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'default' | 'priority'>('default');
  const [selectedExperiment, setSelectedExperiment] = useState<Experiment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [experiments, setExperiments] = useState<Experiment[]>([
    {
      id: '1',
      title: 'AI-Powered Personalisation',
      hypothesis: 'Implementing AI-driven content personalisation will increase user engagement by 25%',
      status: 'running',
      confidence: 78,
      impact: 'high',
      signals: ['engagement', 'retention', 'conversion'],
      startDate: '2024-01-15',
      expectedEnd: '2024-02-15',
    },
    {
      id: '2',
      title: 'Mobile-First Checkout',
      hypothesis: 'Redesigning checkout for mobile will reduce cart abandonment by 30%',
      status: 'completed',
      confidence: 92,
      impact: 'high',
      signals: ['conversion', 'mobile', 'UX'],
      startDate: '2023-12-01',
      expectedEnd: '2024-01-01',
      results: 'Achieved 28% reduction in cart abandonment. Rolling out to all users.',
    },
    {
      id: '3',
      title: 'Subscription Tier Pricing',
      hypothesis: 'Introducing a mid-tier subscription will increase revenue per user by 15%',
      status: 'proposed',
      confidence: 65,
      impact: 'medium',
      signals: ['revenue', 'pricing', 'customer'],
      startDate: '2024-02-01',
      expectedEnd: '2024-03-15',
    },
    {
      id: '4',
      title: 'Social Media Integration',
      hypothesis: 'Adding social sharing features will increase organic growth by 20%',
      status: 'failed',
      confidence: 45,
      impact: 'medium',
      signals: ['growth', 'social', 'viral'],
      startDate: '2023-11-01',
      expectedEnd: '2023-12-15',
      results: 'Only achieved 5% increase. Users preferred direct sharing methods.',
    },
    {
      id: '5',
      title: 'Voice Interface Beta',
      hypothesis: 'Voice commands will improve accessibility and user satisfaction scores by 40%',
      status: 'running',
      confidence: 71,
      impact: 'high',
      signals: ['accessibility', 'satisfaction', 'innovation'],
      startDate: '2024-01-20',
      expectedEnd: '2024-03-01',
    },
  ]);

  // Listen for new experiments created from market insights
  useEffect(() => {
    const handleNewExperiment = (event: CustomEvent) => {
      const newExperiment = event.detail;
      setExperiments(prev => [newExperiment, ...prev]);
      
      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.innerHTML = `
        <div class="flex items-center space-x-2">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
          </svg>
          <span>New experiment added from market insight!</span>
        </div>
      `;
      document.body.appendChild(notification);
      
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 5000);
    };

    window.addEventListener('new-experiment-created', handleNewExperiment as EventListener);
    return () => {
      window.removeEventListener('new-experiment-created', handleNewExperiment as EventListener);
    };
  }, []);

  // Listen for AI chat messages to trigger reordering
  useEffect(() => {
    const handleAIMessage = (event: CustomEvent) => {
      const message = event.detail.message?.toLowerCase() || '';
      if (message.includes('re-prioritise') || message.includes('prioritise') || message.includes('experiment queue')) {
        setSortOrder('priority');
        // Show a brief notification that reordering occurred
        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-pulse';
        notification.textContent = '🔄 Experiments re-prioritised by impact + confidence';
        document.body.appendChild(notification);
        setTimeout(() => {
          if (document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
        }, 3000);
      }
    };

    window.addEventListener('ai-chat-message', handleAIMessage as EventListener);
    return () => {
      window.removeEventListener('ai-chat-message', handleAIMessage as EventListener);
    };
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'proposed': return 'bg-blue-900 text-blue-300 border-blue-600';
      case 'running': return 'bg-yellow-900 text-yellow-300 border-yellow-600';
      case 'completed': return 'bg-green-900 text-green-300 border-green-600';
      case 'failed': return 'bg-red-900 text-red-300 border-red-600';
      default: return 'bg-gray-900 text-gray-300 border-gray-600';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'text-red-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'proposed': return <Clock className="w-4 h-4" />;
      case 'running': return <FlaskConical className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getCreatedFromIcon = (createdFrom?: string) => {
    switch (createdFrom) {
      case 'market-insight': return <Lightbulb className="w-4 h-4 text-yellow-400" />;
      case 'backlog': return <Activity className="w-4 h-4 text-blue-400" />;
      default: return null;
    }
  };

  // Calculate priority score based on impact and confidence
  const calculatePriorityScore = (experiment: Experiment) => {
    const impactScore = experiment.impact === 'high' ? 3 : experiment.impact === 'medium' ? 2 : 1;
    const confidenceScore = experiment.confidence / 100;
    return impactScore * confidenceScore * 100; // Scale for better sorting
  };

  // Format date range for compact display
  const formatDateRange = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const startStr = start.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
    return `${startStr} - ${endStr}`;
  };

  // Sort experiments based on current sort order
  const sortedExperiments = [...experiments].sort((a, b) => {
    if (sortOrder === 'priority') {
      return calculatePriorityScore(b) - calculatePriorityScore(a);
    }
    return 0; // Default order
  });

  const filteredExperiments = sortedExperiments.filter(exp => {
    const matchesFilter = activeFilter === 'all' || exp.status === activeFilter;
    const matchesSearch = exp.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         exp.hypothesis.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         exp.signals.some(signal => signal.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  const statusCounts = {
    all: experiments.length,
    proposed: experiments.filter(e => e.status === 'proposed').length,
    running: experiments.filter(e => e.status === 'running').length,
    completed: experiments.filter(e => e.status === 'completed').length,
    failed: experiments.filter(e => e.status === 'failed').length,
  };

  const handleViewDetails = (experiment: Experiment) => {
    setSelectedExperiment(experiment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedExperiment(null);
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Experiment Hub</h1>
          <p className="text-gray-400 text-sm lg:text-base">Hypothesis generation, validation, and signal tracking</p>
          {sortOrder === 'priority' && (
            <div className="mt-2 flex items-center space-x-2">
              <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-indigo-400">Sorted by priority (Impact × Confidence)</span>
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mt-4 lg:mt-0">
          {sortOrder === 'priority' && (
            <button 
              onClick={() => setSortOrder('default')}
              className="bg-gray-700 hover:bg-gray-600 text-white px-3 lg:px-4 py-2 rounded-lg transition-colours flex items-center space-x-2"
            >
              <Target className="w-4 h-4" />
              <span>Reset Order</span>
            </button>
          )}
          <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 lg:px-4 py-2 rounded-lg transition-colours flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>New Experiment</span>
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 lg:gap-4">
        {Object.entries(statusCounts).map(([status, count]) => (
          <div
            key={status}
            onClick={() => setActiveFilter(status)}
            className={`p-3 lg:p-4 rounded-lg cursor-pointer transition-colours ${
              activeFilter === status
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <div className="text-xl lg:text-2xl font-bold">{count}</div>
            <div className="text-xs lg:text-sm capitalize">{status}</div>
          </div>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3 lg:gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search experiments, hypotheses, or signals..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-indigo-500 focus:outline-none text-sm lg:text-base"
          />
        </div>
        <button className="bg-gray-800 hover:bg-gray-700 text-white px-3 lg:px-4 py-2 rounded-lg transition-colours flex items-center space-x-2">
          <Filter className="w-4 h-4" />
          <span>Advanced Filters</span>
        </button>
      </div>

      {/* Experiments Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {filteredExperiments.map((experiment, index) => (
          <div key={experiment.id} className={`bg-gray-800 rounded-lg p-4 lg:p-6 border transition-all duration-300 ${
            sortOrder === 'priority' 
              ? 'border-indigo-600 hover:border-indigo-500 shadow-lg' 
              : 'border-gray-700 hover:border-gray-600'
          }`}>
            {/* Priority Badge */}
            {sortOrder === 'priority' && (
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-400">Priority Rank:</span>
                  <span className="bg-indigo-600 text-white px-2 py-1 rounded-full text-xs font-bold">
                    #{index + 1}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-400">Score:</span>
                  <span className="text-indigo-400 text-sm font-bold">
                    {Math.round(calculatePriorityScore(experiment))}
                  </span>
                </div>
              </div>
            )}

            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <h3 className="text-base lg:text-lg font-semibold text-white">{experiment.title}</h3>
                  {experiment.createdFrom && (
                    <div className="flex items-center space-x-1 px-2 py-1 bg-gray-700 rounded-full">
                      {getCreatedFromIcon(experiment.createdFrom)}
                      <span className="text-xs text-gray-400 capitalize">
                        {experiment.createdFrom.replace('-', ' ')}
                      </span>
                    </div>
                  )}
                </div>
                <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs border ${getStatusColor(experiment.status)}`}>
                  {getStatusIcon(experiment.status)}
                  <span className="capitalize">{experiment.status}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-400">Impact</div>
                <div className={`text-base lg:text-lg font-bold capitalize ${getImpactColor(experiment.impact)}`}>
                  {experiment.impact}
                </div>
              </div>
            </div>

            {/* Hypothesis */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-300 mb-2">Hypothesis</h4>
              <p className="text-sm text-gray-400">{experiment.hypothesis}</p>
            </div>

            {/* Additional details for experiments created from insights */}
            {experiment.createdFrom === 'market-insight' && experiment.objective && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-300 mb-2">Objective</h4>
                <p className="text-sm text-gray-400">{experiment.objective}</p>
              </div>
            )}

            {/* Metrics */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-sm text-gray-400">Confidence</div>
                <div className="text-sm font-medium text-white">{experiment.confidence}%</div>
              </div>
              <div>
                <div className="text-sm text-gray-400">Duration</div>
                <div className="text-xs text-white font-medium">
                  {formatDateRange(experiment.startDate, experiment.expectedEnd)}
                </div>
              </div>
            </div>

            {/* Enhanced details for market insight experiments */}
            {experiment.createdFrom === 'market-insight' && (
              <div className="grid grid-cols-2 gap-4 mb-4">
                {experiment.methodology && (
                  <div>
                    <div className="text-sm text-gray-400">Methodology</div>
                    <div className="text-xs text-white font-medium">{experiment.methodology}</div>
                  </div>
                )}
                {experiment.sampleSize && (
                  <div>
                    <div className="text-sm text-gray-400">Sample Size</div>
                    <div className="text-xs text-white font-medium">{experiment.sampleSize.toLocaleString()}</div>
                  </div>
                )}
              </div>
            )}

            {/* Signals */}
            <div className="mb-4">
              <div className="text-sm text-gray-400 mb-2">Signal Tags</div>
              <div className="flex flex-wrap gap-2">
                {experiment.signals.map((signal, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-gray-700 text-gray-300 rounded-full text-xs"
                  >
                    {signal}
                  </span>
                ))}
              </div>
            </div>

            {/* Results */}
            {experiment.results && (
              <div className="mb-4">
                <div className="text-sm text-gray-400 mb-2">Results</div>
                <p className="text-sm text-gray-300">{experiment.results}</p>
              </div>
            )}

            {/* Source insight link */}
            {experiment.sourceInsight && (
              <div className="mb-4 p-3 bg-gray-700 rounded-lg">
                <div className="text-sm text-gray-400 mb-1">Created from Market Insight:</div>
                <div className="text-sm text-indigo-400 font-medium">{experiment.sourceInsight.title}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {experiment.sourceInsight.category} • Confidence: {experiment.sourceInsight.confidence}%
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-700">
              <div className="flex flex-col space-y-1">
                <div className="flex items-center space-x-2">
                  <Target className="w-4 h-4 text-indigo-400" />
                  <span className="text-sm text-gray-400">ID: {experiment.id}</span>
                </div>
                {sortOrder === 'priority' && (
                  <span className="text-xs text-indigo-400">
                    (Impact: {experiment.impact} × Confidence: {experiment.confidence}%)
                  </span>
                )}
                {experiment.owner && (
                  <span className="text-xs text-gray-500">Owner: {experiment.owner}</span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <button className="text-indigo-400 hover:text-indigo-300 transition-colours">
                  <BarChart3 className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleViewDetails(experiment)}
                  className="text-gray-400 hover:text-gray-300 transition-colours text-sm hover:bg-gray-700 px-2 py-1 rounded"
                >
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredExperiments.length === 0 && (
        <div className="text-center py-12">
          <FlaskConical className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-300 mb-2">No experiments found</h3>
          <p className="text-gray-500">Try adjusting your search or filter criteria</p>
        </div>
      )}

      {/* Experiment Details Modal */}
      <ExperimentDetailsModal
        experiment={selectedExperiment}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default ExperimentHub;